import React, { useState, useMemo, useCallback  } from "react";
import debounce from "lodash.debounce";
import { toast } from "react-toastify";
import { Tabs } from "../../components/Common";
import type { Tab } from '../../components/Common';
import { VendorsTable } from "../../components/Tables";
import { Box } from "@mui/material";
import { PageHeader } from "../../components/Layout";
import { DefaultFeeModal } from "../../components/Vendors/DefaultFeeModal";

const Vendors: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'suspended'>('all');
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [feeModalOpen, setFeeModalOpen] = useState(false);

  const tabs: Tab[] = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "suspended", label: "Suspended" },
  ];
    // Create a debounced version of the search handler
  const debouncedChangeHandler = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
      }, 400), // waits 400ms after typing stops
    []
  );

    // This fires immediately on every keystroke but updates the debounced value later
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);
      debouncedChangeHandler(value);
    },
    [debouncedChangeHandler]
  );

  const handleFeeModalSuccess = () => {
    toast.success('Default fee updated successfully');
  };

  return (
    <Box
      className="page-container"
      sx={{
        padding: {
          xs: "24px 16px",
          sm: "40px 40px",
          md: "40px 80px",
        },
        gap: "24px",
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Page Header */}
      <PageHeader
        title="Vendors"
        showSearch
        searchPlaceholder="Search vendors"
        searchValue={searchQuery}
        onSearchChange={handleSearchChange}
        primaryButton={true}
        actionButtonText="Edit Default Fee"
        onActionClick={() => setFeeModalOpen(true)}
      />

      {/* Tabs and Table Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignSelf: "stretch",
          gap: "32px",
          width: "100%",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {/* Tabs */}
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(tabKey) => setActiveTab(tabKey as typeof activeTab)}
        />

        {/* Tab Content */}
        {activeTab === "all" && <VendorsTable searchQuery={debouncedSearch} />}
        {activeTab === "active" && <VendorsTable searchQuery={debouncedSearch} filterStatus="active" />}
        {activeTab === "suspended" && <VendorsTable searchQuery={debouncedSearch} filterStatus="suspended" />}
      </Box>

      {/* Default Fee Modal */}
      <DefaultFeeModal
        open={feeModalOpen}
        onClose={() => setFeeModalOpen(false)}
        onSuccess={handleFeeModalSuccess}
      />
    </Box>
  );
};
export default Vendors;
